// kontaktKarten.tsx — Bausteine, die Tabelle und Handy-Karten der Kontaktliste
// teilen (User 28.09.2026): die Zusatzzeile der Arbeitsansicht, die
// T/S/Z-Badges und die Abschnitte der Arbeitsansicht als Kartenliste.
//
// Die Abschnitte selbst (welche es gibt, was drin steht, Köpfe mit Zähler)
// baut `ContactTableRedesign` EINMAL als `DataTableAbschnitt[]` — die Tabelle
// rendert sie als Kopfzeilen, das Handy hier als Kopf über Karten.

import * as React from "react";
import type { DataTableAbschnitt } from "./DataTable";
import { VorgangBadge } from "./cells";
import type { UmfeldRolle } from "./trefferUmfeld";
import "./unternehmensverbund.css";

/** Zeile unter dem Namen in der Arbeitsansicht. */
export type ArbeitsZusatz =
  | { art: "gb"; text: string }
  | { art: "mail"; zeit: string; betreff: string }
  | {
      art: "anruf";
      zeit: string;
      nummer: string | null;
      anzahl: number;
      /** Nur über die Rufgruppe, nicht persönlich. */
      team: boolean;
      /** Fehlt, wenn keine Nummer bekannt ist. */
      onZurueckrufen?: () => void;
      onErledigt: () => void;
      /** Erledigen läuft gerade — Knopf gesperrt. */
      laeuft?: boolean;
    };

/** Klick im Zusatz darf die Zeile (Akte öffnen) nicht mit auslösen. */
const nurHier = (fn: () => void) => (e: React.MouseEvent) => {
  e.stopPropagation();
  fn();
};

/** „wird heute 54", Uhrzeit + Betreff der neuen Mail bzw. der verpasste Anruf. */
export function ArbeitsZusatzZeile({ zusatz }: { zusatz?: ArbeitsZusatz | null }) {
  if (!zusatz) return null;
  if (zusatz.art === "anruf") {
    return (
      <span className="kt-zusatz anruf" data-testid="verpasst-zusatz">
        <span className="zeit">{zusatz.zeit}</span>
        {zusatz.nummer ? <span className="nummer">{zusatz.nummer}</span> : null}
        {zusatz.anzahl > 1 ? (
          <span className="anzahl" title={`${zusatz.anzahl} verpasste Anrufe diese Woche`}>
            {zusatz.anzahl}×
          </span>
        ) : null}
        {zusatz.team ? (
          <span className="team" title="Verpasst auf einer Rufgruppe, in der du bist">
            Team
          </span>
        ) : null}
        <span className="knoepfe">
          {zusatz.onZurueckrufen ? (
            <button type="button" className="kt-anruf-knopf ruf" onClick={nurHier(zusatz.onZurueckrufen)}>
              Zurückrufen
            </button>
          ) : null}
          <button
            type="button"
            className="kt-anruf-knopf"
            disabled={zusatz.laeuft}
            onClick={nurHier(zusatz.onErledigt)}
            title={zusatz.anzahl > 1 ? `Alle ${zusatz.anzahl} Anrufe als erledigt markieren` : "Als erledigt markieren"}
          >
            Erledigt
          </button>
        </span>
      </span>
    );
  }
  return zusatz.art === "gb" ? (
    <span className="kt-zusatz gb">{zusatz.text}</span>
  ) : (
    <span className="kt-zusatz mail" title={zusatz.betreff}>
      <span className="zeit">{zusatz.zeit}</span>
      <span className="betreff">{zusatz.betreff || "(ohne Betreff)"}</span>
    </span>
  );
}

/**
 * Rolle einer Umfeld-Zeile unter dem Namen (Verbund/Haushalt des Treffers,
 * `trefferUmfeld.ts`): „Tochterfirma", „Geschäftsführer · AllcuraMed GmbH".
 * Beendete Rollen durchgestrichen mit „ausgeschieden MM/JJJJ".
 */
export function UmfeldRolleZeile({ rolle }: { rolle?: UmfeldRolle | null }) {
  if (!rolle?.teile.length) return null;
  const titel = rolle.teile.map((t) => t.text + (t.ausgeschieden ? ` (ausgeschieden${t.ende ? ` ${t.ende}` : ""})` : "")).join("; ");
  return (
    <span className="kt-zusatz umfeld" title={titel}>
      <span className="umfeld-text">
        {rolle.teile.map((t, i) => (
          <React.Fragment key={i}>
            {i > 0 ? "; " : ""}
            {t.ausgeschieden ? (
              <>
                <s>{t.text}</s>
                <span className="umfeld-aus"> ausgeschieden{t.ende ? ` ${t.ende}` : ""}</span>
              </>
            ) : (
              t.text
            )}
          </React.Fragment>
        ))}
      </span>
    </span>
  );
}

/**
 * Termin / Schaden / Zahlungsverkehr — liefert der Server nur in der
 * Arbeitsansicht (`?include=arbeit`); sonst steht hier nichts.
 */
export function OffenePunkteBadges({ openEventTypes }: { openEventTypes?: string[] | null }) {
  const t = openEventTypes ?? [];
  return (
    <>
      {t.includes("T") ? <VorgangBadge label="T" className="vb-t" title="Anstehender Termin (nächste 14 Tage)" /> : null}
      {t.includes("S") ? <VorgangBadge label="S" className="vb-s" title="Offener Schaden" /> : null}
      {t.includes("Z") ? <VorgangBadge label="Z" className="vb-z" title="Offener Zahlungsverkehr" /> : null}
    </>
  );
}

/** Die Abschnitte der Arbeitsansicht als Kartenliste (Handy). */
export function KartenAbschnitte<T>({
  abschnitte,
  rowKey,
  karte,
}: {
  abschnitte: DataTableAbschnitt<T>[];
  rowKey: (row: T) => React.Key;
  karte: (row: T) => React.ReactNode;
}) {
  return (
    <>
      {abschnitte.map((a) => (
        <section key={a.key} className={"ct-sec kt-sec " + (a.className ?? "")}>
          {a.kopf != null ? <div className="ct-sec-kopf">{a.kopf}</div> : null}
          {a.teile.map((t) => (
            <React.Fragment key={t.key}>
              {t.titel ? <div className="ct-sec-titel">{t.titel}</div> : null}
              {t.zeilen.map((r) => (
                <React.Fragment key={rowKey(r)}>{karte(r)}</React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </section>
      ))}
    </>
  );
}
